/**
 * MUI theme — built from shared design tokens. Both light and dark palettes
 * derive from the same source so any later token change updates both modes.
 */

import { createTheme, type Theme } from "@mui/material/styles";
import {
  fontFamilies,
  fontWeights,
  lineHeights,
  palette,
  radii,
  type PaletteMode,
} from "@ottopilot/shared";

export function buildTheme(mode: PaletteMode = "light"): Theme {
  const p = palette[mode];

  return createTheme({
    palette: {
      mode,
      primary: { main: p.primary, contrastText: p.primaryContrast },
      secondary: { main: p.accent },
      background: { default: p.bg, paper: p.bgElevated },
      text: { primary: p.text, secondary: p.textMuted },
      success: { main: p.success },
      warning: { main: p.warning },
      error: { main: p.danger },
      info: { main: p.info },
      divider: p.border,
    },
    shape: { borderRadius: radii.md },
    typography: {
      fontFamily: fontFamilies.sans,
      fontWeightRegular: fontWeights.regular,
      fontWeightMedium: fontWeights.medium,
      fontWeightBold: fontWeights.bold,
      h1: {
        fontFamily: fontFamilies.display,
        fontWeight: fontWeights.semibold,
        fontSize: "2.5rem",
        lineHeight: lineHeights.tight,
        letterSpacing: "-0.01em",
      },
      h2: {
        fontFamily: fontFamilies.display,
        fontWeight: fontWeights.semibold,
        fontSize: "2rem",
        lineHeight: lineHeights.tight,
      },
      h3: {
        fontFamily: fontFamilies.display,
        fontWeight: fontWeights.medium,
        fontSize: "1.5rem",
        lineHeight: lineHeights.snug,
      },
      h4: {
        fontFamily: fontFamilies.sans,
        fontWeight: fontWeights.semibold,
        fontSize: "1.25rem",
        lineHeight: lineHeights.snug,
      },
      body1: { fontSize: "1rem", lineHeight: lineHeights.normal },
      body2: { fontSize: "0.875rem", lineHeight: lineHeights.normal },
      button: { textTransform: "none", fontWeight: fontWeights.medium },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radii.md, paddingInline: 16 },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
    },
  });
}

export const lightTheme = buildTheme("light");
export const darkTheme = buildTheme("dark");
